import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatPaginator, MatSort, MatDialog, SortDirection, MatSelect, MatOption } from "@angular/material";
import { Store, Action } from '@ngrx/store';
import { Router } from '@angular/router';
import { Actions } from '@ngrx/effects';
import * as submissionActions from '../store/actions';
import { UploadDialogComponent } from './upload-dialog.component';
import { ModuleConfiguration } from '../submission.module.config';
import { StoreDataSource } from '../../core/services/StoreDataSource';
import { JobListModel } from '../models/JobListModel';
import { JobModel } from '../models/JobModel';
import { TileState } from '../../pillar/dashboard/store/state/TileState';
import * as fromStore from '../store/reducers';
import * as actions from '../../pillar/dashboard/store/actions';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import { BaseAddressInterceptor } from '../../core/interceptors/BaseAddressInterceptor';
import { SearchComponent } from '../../pillar/search/components/search.component';
import { ACTION_ERROR, ActionError } from '../../pillar/common/actions/ActionError';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { FormGroup, FormControl } from '@angular/forms';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { ActionBase } from '../../core/store/action/ActionBase';
import { AdvancedFilterModel } from '../models/AdvancedFilterModel';
import { LookupModel } from '../../common/models/LookupModel';
import { UserService } from '../../customer/services/UserService';
import { DataWarehouseService } from '../../core/services';
import { JobsService } from '../services/JobsService';
import { JobExportSelectionCriteriaDialogComponent } from './job-export-selection-criteria-dialog.component';
import { AdvancedFilterDialogComponent } from './advanced-filter-dialog.component';
import { DatabaseJobsFilterDialogComponent } from './database-jobs-filter-dialog.component';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';

@Component({
  selector: 'brd-submission-dashboard',
  templateUrl: './submission-dashboard.component.html',
  styleUrls: ['./submission-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubmissionDashboardComponent implements OnInit, OnDestroy {
  static dashboardName = 'submission';
  displayedColumns = ['name', 'submittingEntityName', 'userName', 'createdTimestamp', 'status', 'successfulCount', 'errorCount', 'warningCount', 'actions'];
  jobDataSource: StoreDataSource<JobListModel>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  currentPage$: Observable<number>;
  jobSelected: boolean = false;
  jobKey: string;
  cancelJobSubmissionActionIdentifier: string;
  searchInput = 'SubmissionModule_SearchInput';
  selectedJob_JobKey = 'SubmissionModule_SelectedJobKey';
  sortJob_SortActive = 'SubmissionModule_SortJobActive';
  sortJob_SortDirection = 'SubmissionModule_SortJobDirection';
  uploadPermission: string = 'SubmissionModule.Jobs:UploadFile';
  triggerDatabaseJobsPermission: string = 'SubmissionModule.Jobs:TriggerDatabaseJobs';
  RestartJobPermission: string = 'SubmissionModule.Jobs:RestartJob';
  toppings: FormControl;
  toppingList: string[];
  advancedFilterForm: FormGroup;
  actionIdentifiers: string[] = [];
  statuses: LookupModel[];
  submittingEntities: LookupModel[];
  users: LookupModel[];
  dataSets: LookupModel[];
  exportJobsPermission = 'SubmissionModule.Jobs:Export';
  viewXmlJobReportsPermission = 'SubmissionModule.XmlJobReports:View';
  filterModel: AdvancedFilterModel;
  isUsingPowerBI: boolean = false;

  get name(): string {
    return SubmissionDashboardComponent.dashboardName;
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(SearchComponent) search: SearchComponent;
  @ViewChild(AlertsComponent) alerts: AlertsComponent;

  constructor(private store: Store<TileState<JobListModel, JobModel>>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private dialog: MatDialog,
    private curentUserService: CurrentUserService,
    private baseAddressInterceptor: BaseAddressInterceptor,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private dataWarehouseService: DataWarehouseService,
    private jobService: JobsService,
    private appConfigurationService: AppConfigurationService) {
    this.currentPage$ = this.store.select(fromStore.getJobListCurrentPage);
    this.isUsingPowerBI = appConfigurationService.configuration.isUsingPowerBI;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.checkSelected();

    this.userService.getCurrentUserSubmittingEntities().subscribe(result => {
      this.submittingEntities = result.model;
    });
    this.dataWarehouseService.getDataSets().subscribe(result => {
      this.dataSets = result;
    });
    this.userService.getCurrentUserUsers().subscribe(result => {
      this.users = result.model;
    });
    this.jobService.getJobStatuses().subscribe(result => {
      this.statuses = result.model;
    });

    this.refreshDataSource();

    this.actions$.ofType(ACTION_ERROR)
      .filter((a: ActionError) => this.cancelJobSubmissionActionIdentifier && a.payload.action.identifier === this.cancelJobSubmissionActionIdentifier)
      .takeUntil(this.destroyed$)
      .subscribe((p: ActionError) => {
        this.cancelJobSubmissionActionIdentifier = undefined;
        for (let error in p.payload.errors) {
          if (p.payload.errors.hasOwnProperty(error)) {
            this.alerts.addError(p.payload.errors[error]);
          }
        }
      });

    this.actions$.ofType(submissionActions.CANCEL_JOB_SUBMISSION_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: submissionActions.CancelJobSubmissionSuccessAction) => {
        this.refresh();
      });
  }

  private refreshDataSource() {
    this.jobDataSource = new StoreDataSource<JobListModel>(
      this.destroyed$,
      this.paginator,
      this.store,
      this.store.select(fromStore.getJobList),
      this.store.select(fromStore.getJobListTotalCount),
      this.store.select(fromStore.getJobListLoading),
      (p, s, search) => new submissionActions.AdvancedFilterAction(ModuleConfiguration.moduleName, JobListModel, {
        page: p.pageIndex,
        pageSize: p.pageSize,
        sort: s.active,
        sortDirection: s.direction,
        searchTerm: search.searchControl.value,
        model: this.filterModel
      }),
      this.sort,
      this.search
    );
  }

  selectItem(item: JobListModel) {
    this.jobSelected = true;
    this.jobKey = item.key;
    sessionStorage.setItem(this.searchInput, this.search.searchControl.value);
    sessionStorage.setItem(this.selectedJob_JobKey, this.jobKey);
    sessionStorage.setItem(this.sortJob_SortActive, this.sort.active);
    sessionStorage.setItem(this.sortJob_SortDirection, this.sort.direction);
    this.router.navigateByUrl('job-workspace/' + item.key);
  }

  cancelItem(item: JobListModel) {
    var cancelJobSubmissionAction = new submissionActions.CancelJobSubmissionAction({ key: item.submittingEntityKey, jobKey: item.key });
    this.cancelJobSubmissionActionIdentifier = cancelJobSubmissionAction.identifier;
    this.store.dispatch(cancelJobSubmissionAction);
  }

  checkSelected() {
    var jobKey = sessionStorage.getItem(this.selectedJob_JobKey);
    if (jobKey) {
      this.jobSelected = true;
      this.jobKey = jobKey;
    }
    var searchInput = sessionStorage.getItem(this.searchInput);
    if (searchInput) {
      this.search.searchControl.setValue(searchInput);
    }
    var sortActive = sessionStorage.getItem(this.sortJob_SortActive);
    var sortDirection = sessionStorage.getItem(this.sortJob_SortDirection) as SortDirection;
    if (sortActive && sortDirection) {
      this.sort.active = sortActive;
      this.sort.direction = sortDirection;
    }
  }

  showUpload(): void {
    var dialogRef = this.dialog.open(UploadDialogComponent, {
      panelClass: 'customized-dialog'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.refresh();
    });
  }

  refresh(model = null): void {
    this.store.dispatch(new submissionActions.AdvancedFilterAction(
      ModuleConfiguration.moduleName,
      JobListModel,
      { page: 0, pageSize: 20, model: model }));
  }

  downloadExcelFile(jobKey: string): void {
    if (jobKey != null){
      this.jobService.getJobExportToExcel(jobKey).take(1)
        .subscribe(r => {
          this.jobService.openExtract(r.model.url, r.model);
      })
    }
  }

  downloadPdfFile(jobKey: string): void {
    if (jobKey != null){
      this.jobService.getJobExportToPdf(jobKey).take(1)
        .subscribe(r => {
          this.jobService.openExtract(r.model.url, r.model);
      })
    }
  }

  downloadJobReport(jobKey: string): void {
    // open url in new tab
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['portal/job-report/' + jobKey])
    )
    window.open(url, '_blank')
  }

  downloadXmlFile(jobKey: string): void {
    if (jobKey != null){
      this.jobService.getJobExportToXml(jobKey).take(1)
        .subscribe(r => {
          this.jobService.openExtract(r.model.url, r.model);
      })
    }
  }

  advancedFilter() {
    var dialogRef = this.dialog.open(AdvancedFilterDialogComponent, {
      panelClass: 'customized-dialog',
      width: '648px',
      data: {
        model: this.filterModel
      }
    });
    dialogRef.afterClosed().subscribe((model) => {
      if(model == ""){
        this.refresh();
      }else{
        this.refresh(model);
        this.filterModel = model;
      }     
    });
  }

  lookupCompare(l1: LookupModel, l2: LookupModel) {
    return l1 && l2 ? l1.key === l2.key : l1 === l2;
  }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }

  private calculateProgressBar(job: JobListModel): number {
    return Math.round((job.rootEntitiesProcessedCount / job.rootEntitiesCount + 0.00001) * 100);
  }

  showExportJobsDialog(): void {
    this.dialog.open(JobExportSelectionCriteriaDialogComponent, {
      panelClass: 'customized-dialog',
      width: '648px',
      ariaLabel: 'Job Export Selection Criteria'
    });
  }

  selectDatabaseJobs(): void {
    var dialogRef = this.dialog.open(DatabaseJobsFilterDialogComponent, {
      panelClass: 'customized-dialog',
      ariaLabel: 'Database Jobs Selection Criteria'
    });
    dialogRef.afterClosed().subscribe(() => {
      this.refresh();
    });
  }

  hasPermission(permission: string): boolean {
    return this.curentUserService.hasPermission(permission);
  }

  RestartJob(item: JobListModel) {
    if (item.key != null){
      this.jobService.RestartJob(item.key)
        .subscribe(message => {
          if(message)
          {
            this.refresh();
            this.alerts.addInformation(message);
          }
      })
    }
  }

  DateDiff(date:Date) {
    var currentDate = new Date();
    var createdTimestamp = new Date(date);
    var hours = Math.floor((currentDate.getTime() - createdTimestamp.getTime()) / 1000 / 60 / 60);
    return hours;
  }

  ShowRestartJobBtn(item: JobListModel){
    return (this.hasPermission(this.RestartJobPermission) && item != null && item.status === 'Running' && this.DateDiff(item.createdTimestamp) >= 3);
  }
}


// WEBPACK FOOTER //
// ./src/app/submission/components/submission-dashboard.component.ts